import { useMemo } from "react";
import { useDynamicConfig } from "@statsig/react-bindings";
import { createIntl, createIntlCache } from "react-intl";

import {
  BAZAAR_CURRENCY_DEFAULTS_CONFIG,
  getCurrencyFractionDigits,
  getZeroDecimalCurrencyCodes,
  MICROS_PER_CURRENCY_UNIT,
  normalizeCurrencyCode,
  parseConfiguredCurrencyAmount,
  ZERO_DECIMAL_CURRENCY_CODES_CONFIG_KEY,
} from "./CurrencyDefaults";
import { messages } from "./localization/CampaignBudget.messages";

import type { IntlShape } from "react-intl";
import type { components } from "@oai/sofa-client/components";
import type { ZeroDecimalCurrencyCodes } from "./CurrencyDefaults";

export type CampaignBudgetType = "daily" | "lifetime";

type CampaignBudget = components["schemas"]["CampaignsResource"]["data"]["0"]["budget"];

export const ADS_MANAGER_DEFAULT_CAMPAIGN_CURRENCY_AMOUNT_CONFIG =
  "ads_manager_default_campaign_currency_amount";
export const CAMPAIGN_BUDGET_MICROS_PER_UNIT = MICROS_PER_CURRENCY_UNIT;
export const DEFAULT_DAILY_CAMPAIGN_BUDGET_INPUT = "100.00";
export const DEFAULT_LIFETIME_CAMPAIGN_BUDGET_INPUT = "500.00";
export const MIN_LIFETIME_CAMPAIGN_BUDGET_MICROS = 100 * CAMPAIGN_BUDGET_MICROS_PER_UNIT;
export const MIN_DAILY_CAMPAIGN_BUDGET_MICROS = 25 * CAMPAIGN_BUDGET_MICROS_PER_UNIT;
const DEFAULT_MIN_LIFETIME_CAMPAIGN_BUDGET_INPUT = "100.00";
const DEFAULT_MIN_DAILY_CAMPAIGN_BUDGET_INPUT = "25.00";
const DEFAULT_MIN_LIFETIME_CAMPAIGN_BUDGET_BY_CURRENCY_CONFIG_KEY =
  "default_min_lifetime_budget_amount_by_currency";
const DEFAULT_MIN_DAILY_CAMPAIGN_BUDGET_BY_CURRENCY_CONFIG_KEY =
  "default_min_daily_budget_amount_by_currency";
const DEFAULT_INTL = createIntl({ locale: "en" }, createIntlCache());

export type DailyCampaignBudgetConfig = {
  defaultDailyCampaignBudgetInput: string;
  minDailyCampaignBudgetMicros: number;
  minLifetimeCampaignBudgetMicros: number;
};

type CampaignBudgetInputByCurrency = Record<string, string | undefined>;

export function getCampaignBudgetInputsByCurrency(
  value: unknown,
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes,
): CampaignBudgetInputByCurrency {
  if (typeof value !== "object" || value == null || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value as Record<string, unknown>).reduce<CampaignBudgetInputByCurrency>(
    (defaultsByCurrency, [currency, amount]) => {
      const normalizedCurrency = normalizeCurrencyCode(currency);
      const parsedAmount = parseConfiguredCurrencyAmount({
        value: amount,
        currency: normalizedCurrency,
        zeroDecimalCurrencyCodes,
      });
      if (!normalizedCurrency || parsedAmount == null) {
        return defaultsByCurrency;
      }
      return {
        ...defaultsByCurrency,
        [normalizedCurrency]: parsedAmount,
      };
    },
    {},
  );
}

export function getDefaultMinDailyCampaignBudgetInputForCurrency({
  currency,
  defaultMinDailyCampaignBudgetInputsByCurrency,
  zeroDecimalCurrencyCodes,
}: {
  currency: string | null | undefined;
  defaultMinDailyCampaignBudgetInputsByCurrency: CampaignBudgetInputByCurrency;
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
}): string {
  const normalizedCurrency = normalizeCurrencyCode(currency);
  if (!normalizedCurrency) {
    return DEFAULT_MIN_DAILY_CAMPAIGN_BUDGET_INPUT;
  }
  return (
    defaultMinDailyCampaignBudgetInputsByCurrency[normalizedCurrency] ??
    Number(DEFAULT_MIN_DAILY_CAMPAIGN_BUDGET_INPUT).toFixed(
      getCurrencyFractionDigits(normalizedCurrency, zeroDecimalCurrencyCodes),
    )
  );
}

export function getDefaultMinLifetimeCampaignBudgetInputForCurrency({
  currency,
  defaultMinLifetimeCampaignBudgetInputsByCurrency,
  zeroDecimalCurrencyCodes,
}: {
  currency: string | null | undefined;
  defaultMinLifetimeCampaignBudgetInputsByCurrency: CampaignBudgetInputByCurrency;
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
}): string {
  const normalizedCurrency = normalizeCurrencyCode(currency);
  if (!normalizedCurrency) {
    return DEFAULT_MIN_LIFETIME_CAMPAIGN_BUDGET_INPUT;
  }
  return (
    defaultMinLifetimeCampaignBudgetInputsByCurrency[normalizedCurrency] ??
    Number(DEFAULT_MIN_LIFETIME_CAMPAIGN_BUDGET_INPUT).toFixed(
      getCurrencyFractionDigits(normalizedCurrency, zeroDecimalCurrencyCodes),
    )
  );
}

export function getDefaultDailyCampaignBudgetInputForCurrency({
  currency,
  defaultDailyCampaignBudgetInputsByCurrency,
  zeroDecimalCurrencyCodes,
}: {
  currency: string | null | undefined;
  defaultDailyCampaignBudgetInputsByCurrency: CampaignBudgetInputByCurrency;
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
}): string {
  const normalizedCurrency = normalizeCurrencyCode(currency);
  return (
    (normalizedCurrency ? defaultDailyCampaignBudgetInputsByCurrency[normalizedCurrency] : null) ??
    Number(DEFAULT_DAILY_CAMPAIGN_BUDGET_INPUT).toFixed(
      getCurrencyFractionDigits(normalizedCurrency, zeroDecimalCurrencyCodes),
    )
  );
}

function budgetInputToMicros(budgetInput: string): number {
  return Math.round(Number(budgetInput) * CAMPAIGN_BUDGET_MICROS_PER_UNIT);
}

function formatBudgetInputFromMicrosForCurrency({
  amountMicros,
  currency,
  zeroDecimalCurrencyCodes,
}: {
  amountMicros: number;
  currency: string | null | undefined;
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
}): string {
  return (amountMicros / CAMPAIGN_BUDGET_MICROS_PER_UNIT).toFixed(
    getCurrencyFractionDigits(currency, zeroDecimalCurrencyCodes),
  );
}

export function getDefaultDailyCampaignBudgetInput(
  defaultMinDailyCampaignBudgetInput = DEFAULT_MIN_DAILY_CAMPAIGN_BUDGET_INPUT,
  options: {
    currency?: string | null | undefined;
    defaultDailyCampaignBudgetInput?: string;
    zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
  } = {},
) {
  return formatBudgetInputFromMicrosForCurrency({
    amountMicros: Math.max(
      budgetInputToMicros(
        options.defaultDailyCampaignBudgetInput ?? DEFAULT_DAILY_CAMPAIGN_BUDGET_INPUT,
      ),
      budgetInputToMicros(defaultMinDailyCampaignBudgetInput),
    ),
    currency: options.currency,
    zeroDecimalCurrencyCodes: options.zeroDecimalCurrencyCodes,
  });
}

export function getDailyCampaignBudgetConfig({
  currency,
  defaultDailyCampaignBudgetInputsByCurrency = {},
  defaultMinDailyCampaignBudgetInputsByCurrency,
  defaultMinLifetimeCampaignBudgetInputsByCurrency,
  zeroDecimalCurrencyCodes,
}: {
  currency: string | null | undefined;
  defaultDailyCampaignBudgetInputsByCurrency?: CampaignBudgetInputByCurrency;
  defaultMinDailyCampaignBudgetInputsByCurrency: CampaignBudgetInputByCurrency;
  defaultMinLifetimeCampaignBudgetInputsByCurrency: CampaignBudgetInputByCurrency;
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
}): DailyCampaignBudgetConfig {
  const defaultMinDailyCampaignBudgetInput = getDefaultMinDailyCampaignBudgetInputForCurrency({
    currency,
    defaultMinDailyCampaignBudgetInputsByCurrency,
    zeroDecimalCurrencyCodes,
  });
  const defaultDailyCampaignBudgetInput = getDefaultDailyCampaignBudgetInputForCurrency({
    currency,
    defaultDailyCampaignBudgetInputsByCurrency,
    zeroDecimalCurrencyCodes,
  });
  const defaultMinLifetimeCampaignBudgetInput = getDefaultMinLifetimeCampaignBudgetInputForCurrency(
    {
      currency,
      defaultMinLifetimeCampaignBudgetInputsByCurrency,
      zeroDecimalCurrencyCodes,
    },
  );
  return {
    defaultDailyCampaignBudgetInput: getDefaultDailyCampaignBudgetInput(
      defaultMinDailyCampaignBudgetInput,
      {
        currency,
        defaultDailyCampaignBudgetInput,
        zeroDecimalCurrencyCodes,
      },
    ),
    minDailyCampaignBudgetMicros: budgetInputToMicros(defaultMinDailyCampaignBudgetInput),
    minLifetimeCampaignBudgetMicros: budgetInputToMicros(defaultMinLifetimeCampaignBudgetInput),
  };
}

export function useDailyCampaignBudgetConfig({
  currency,
}: {
  currency: string | null | undefined;
}): DailyCampaignBudgetConfig {
  const currencyDefaultsConfig = useDynamicConfig(BAZAAR_CURRENCY_DEFAULTS_CONFIG);
  const defaultCampaignCurrencyAmountConfig = useDynamicConfig(
    ADS_MANAGER_DEFAULT_CAMPAIGN_CURRENCY_AMOUNT_CONFIG,
  );

  return useMemo(() => {
    const zeroDecimalCurrencyCodes = getZeroDecimalCurrencyCodes(
      currencyDefaultsConfig.get(ZERO_DECIMAL_CURRENCY_CODES_CONFIG_KEY, null),
    );
    return getDailyCampaignBudgetConfig({
      currency,
      defaultDailyCampaignBudgetInputsByCurrency: getCampaignBudgetInputsByCurrency(
        defaultCampaignCurrencyAmountConfig.value,
        zeroDecimalCurrencyCodes,
      ),
      defaultMinDailyCampaignBudgetInputsByCurrency: getCampaignBudgetInputsByCurrency(
        currencyDefaultsConfig.get(DEFAULT_MIN_DAILY_CAMPAIGN_BUDGET_BY_CURRENCY_CONFIG_KEY, null),
        zeroDecimalCurrencyCodes,
      ),
      defaultMinLifetimeCampaignBudgetInputsByCurrency: getCampaignBudgetInputsByCurrency(
        currencyDefaultsConfig.get(
          DEFAULT_MIN_LIFETIME_CAMPAIGN_BUDGET_BY_CURRENCY_CONFIG_KEY,
          null,
        ),
        zeroDecimalCurrencyCodes,
      ),
      zeroDecimalCurrencyCodes,
    });
  }, [currencyDefaultsConfig, defaultCampaignCurrencyAmountConfig, currency]);
}

export function getCampaignBudgetInputAfterTypeChange(
  currentType: CampaignBudgetType,
  nextType: CampaignBudgetType,
  currentInput: string,
): string {
  if (currentType !== "daily" || nextType !== "lifetime") {
    return currentInput;
  }

  const currentAmount = Number(currentInput);
  return (
      Number.isFinite(currentAmount) &&
        currentAmount >= Number(DEFAULT_LIFETIME_CAMPAIGN_BUDGET_INPUT)
    ) ?
      currentInput
    : DEFAULT_LIFETIME_CAMPAIGN_BUDGET_INPUT;
}

export function getCampaignBudgetType(budget: CampaignBudget): CampaignBudgetType {
  return budget.daily_spend_limit_micros == null ? "lifetime" : "daily";
}

export function getCampaignBudgetAmountMicros(budget: CampaignBudget): number {
  return budget.daily_spend_limit_micros ?? budget.lifetime_spend_limit_micros ?? 0;
}

export function buildCampaignBudgetPayload(budgetType: CampaignBudgetType, amountMicros: number) {
  if (budgetType === "daily") {
    return { daily_spend_limit_micros: amountMicros };
  }
  return { lifetime_spend_limit_micros: amountMicros };
}

export function getCampaignBudgetLabel(
  budgetType: CampaignBudgetType,
  intl: IntlShape = DEFAULT_INTL,
): string {
  return intl.formatMessage(
    budgetType === "daily" ? messages.dailyBudgetLabel : messages.campaignBudgetLabel,
  );
}

export function formatCampaignBudgetDisplayValue(
  budgetType: CampaignBudgetType,
  formattedAmount: string,
  intl: IntlShape = DEFAULT_INTL,
): string {
  return budgetType === "daily" ?
      intl.formatMessage(messages.dailyBudgetDisplay, { formattedAmount })
    : formattedAmount;
}

export function getCampaignBudgetMinimumMicros(
  budgetType: CampaignBudgetType,
  minDailyCampaignBudgetMicros = MIN_DAILY_CAMPAIGN_BUDGET_MICROS,
  minLifetimeCampaignBudgetMicros = MIN_LIFETIME_CAMPAIGN_BUDGET_MICROS,
): number {
  return budgetType === "daily" ? minDailyCampaignBudgetMicros : minLifetimeCampaignBudgetMicros;
}

export function getCampaignBudgetMinimumAmount(
  budgetType: CampaignBudgetType,
  minDailyCampaignBudgetMicros = MIN_DAILY_CAMPAIGN_BUDGET_MICROS,
  minLifetimeCampaignBudgetMicros = MIN_LIFETIME_CAMPAIGN_BUDGET_MICROS,
): number {
  return (
    getCampaignBudgetMinimumMicros(
      budgetType,
      minDailyCampaignBudgetMicros,
      minLifetimeCampaignBudgetMicros,
    ) / CAMPAIGN_BUDGET_MICROS_PER_UNIT
  );
}

export function getCampaignBudgetMinimumErrorMessage(
  budgetType: CampaignBudgetType,
  intl: IntlShape = DEFAULT_INTL,
  minDailyCampaignBudgetMicros = MIN_DAILY_CAMPAIGN_BUDGET_MICROS,
  minLifetimeCampaignBudgetMicros = MIN_LIFETIME_CAMPAIGN_BUDGET_MICROS,
): string {
  const minimumAmount = getCampaignBudgetMinimumAmount(
    budgetType,
    minDailyCampaignBudgetMicros,
    minLifetimeCampaignBudgetMicros,
  ).toFixed(2);
  return intl.formatMessage(
    budgetType === "daily" ? messages.dailyBudgetMinimumError : messages.campaignBudgetMinimumError,
    { minimumAmount },
  );
}
